<template>
  <section class="account lp-section">
    <div class="lp-container account-inner">
      <header class="account-head">
        <h1 class="lp-h2">{{ $t('dashboard.title') }}</h1>
        <p class="account-sub">{{ $t('dashboard.subtitle') }}</p>
      </header>

      <div v-if="loading" class="account-state">{{ $t('dashboard.loading') }}</div>

      <div v-else-if="errored" class="account-state">
        <p>{{ $t('dashboard.error') }}</p>
        <button class="lp-btn lp-btn--ghost card-btn" @click="load">{{ $t('dashboard.retry') }}</button>
      </div>

      <div v-else class="account-grid">
        <!-- AI usage -->
        <div class="lp-card usage-card">
          <div class="card-head">
            <div class="card-title"><l-icon size="18">gauge</l-icon>{{ $t('dashboard.usage.title') }}</div>
            <span class="period-pill">{{ periodLabel }}</span>
          </div>

          <div class="usage-numbers">
            <span class="usage-pct">{{ percentUsed }}<small>%</small></span>
            <span class="usage-of">{{ $t('dashboard.usage.usedOf', { used: fmt(used), total: fmt(budget) }) }}</span>
          </div>

          <div class="meter-track">
            <div
              class="meter-fill"
              :class="{ 'meter-fill--warn': percentUsed >= 75, 'meter-fill--high': percentUsed >= 95 }"
              :style="{ width: percentUsed + '%' }"
            ></div>
          </div>

          <div class="usage-foot">
            <span>{{ $t('dashboard.usage.remaining', { amount: fmt(remaining) }) }}</span>
            <span class="usage-reset"><l-icon size="14">calendar</l-icon>{{ $t('dashboard.usage.resets', { date: resetDateLabel }) }}</span>
          </div>

          <div class="breakdown">
            <div class="breakdown-title">{{ $t('dashboard.usage.breakdown') }}</div>
            <p v-if="breakdown.length === 0" class="breakdown-empty">{{ $t('dashboard.usage.empty') }}</p>
            <ul v-else class="breakdown-list">
              <li v-for="row in breakdown" :key="row.modelClass" class="breakdown-row">
                <span class="breakdown-label"><span class="dot" :class="'dot--' + row.modelClass"></span>{{ $t('dashboard.classes.' + row.modelClass) }}</span>
                <span class="breakdown-bar"><span class="breakdown-bar-fill" :class="'fill--' + row.modelClass" :style="{ width: row.pct + '%' }"></span></span>
                <span class="breakdown-cost">{{ fmt(row.costMicroUsd) }}</span>
              </li>
            </ul>
          </div>
        </div>

        <!-- Subscription -->
        <div class="lp-card plan-card">
          <div class="card-title"><l-icon size="18">sparkles</l-icon>{{ $t('dashboard.plan.title') }}</div>

          <div class="plan-row">
            <div class="plan-id">
              <span class="plan-name">{{ planLabel }}</span>
              <span v-if="renewLabel" class="plan-meta">{{ renewLabel }}</span>
            </div>
            <span class="status-badge" :class="'status--' + statusKey">{{ $t('dashboard.status.' + statusKey) }}</span>
          </div>

          <ul class="model-access">
            <li v-for="m in billing.models" :key="m.id" :class="{ locked: !m.allowed }">
              <l-icon size="14">{{ m.allowed ? 'check' : 'lock' }}</l-icon>
              <span>{{ m.label }}</span>
            </li>
          </ul>

          <div class="plan-actions">
            <button v-if="!isFree" class="lp-btn lp-btn--ghost card-btn" :disabled="busy" @click="openPortal">
              <l-icon size="15">external-link</l-icon>{{ $t('dashboard.plan.manage') }}
            </button>
            <button class="lp-btn lp-btn--primary card-btn" @click="goPlans">
              {{ isFree ? $t('dashboard.plan.upgrade') : $t('dashboard.plan.viewPlans') }}
            </button>
          </div>
        </div>

        <!-- Account details -->
        <div class="lp-card account-card">
          <div class="card-title"><l-icon size="18">user</l-icon>{{ $t('dashboard.account.title') }}</div>
          <dl class="acc-fields">
            <div v-if="user && user.displayName" class="acc-field">
              <dt>{{ $t('dashboard.account.name') }}</dt>
              <dd>{{ user.displayName }}</dd>
            </div>
            <div class="acc-field">
              <dt>{{ $t('dashboard.account.email') }}</dt>
              <dd>{{ user && user.email }}</dd>
            </div>
          </dl>
          <button class="lp-btn lp-btn--ghost card-btn signout" @click="signOut">
            <l-icon size="15">log-out</l-icon>{{ $t('dashboard.account.signOut') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import {
  getSession,
  getBillingStatus,
  billingPortal,
  logout,
  clearSessionCookies,
} from '@/services/nukliApi';

const MICRO_PER_USD = 1000000;
const CLASS_ORDER = ['frontier', 'standard', 'lite'];
const PLAN_LABEL_KEY = {
  free: 'pricing.free',
  pro: 'pricing.pro',
  pro_plus: 'pricing.proPlus',
  ultra: 'pricing.ultra',
};

export default {
  name: 'AccountDashboard',
  metaInfo() {
    return { title: this.$t('dashboard.title'), titleTemplate: '%s | Nukli' };
  },
  data: () => ({
    billing: null,
    loading: true,
    errored: false,
    busy: false,
  }),
  computed: {
    user() {
      return this.$root.nukliUser || getSession().user;
    },
    used() {
      return this.billing ? this.billing.usedMicroUsd : 0;
    },
    budget() {
      return this.billing ? this.billing.budgetMicroUsd : 0;
    },
    remaining() {
      return Math.max(0, this.budget - this.used);
    },
    percentUsed() {
      if (!this.budget) return 0;
      return Math.min(100, Math.round((this.used / this.budget) * 100));
    },
    breakdown() {
      if (!this.billing || this.used <= 0) return [];
      const byClass = {};
      this.billing.byClass.forEach((c) => { byClass[c.modelClass] = c; });
      return CLASS_ORDER
        .map((modelClass) => {
          const cost = byClass[modelClass] ? byClass[modelClass].costMicroUsd : 0;
          return { modelClass, costMicroUsd: cost, pct: Math.round((cost / this.used) * 100) };
        })
        .filter((row) => row.costMicroUsd > 0);
    },
    isFree() {
      return !this.billing || this.billing.plan === 'free';
    },
    planLabel() {
      const key = PLAN_LABEL_KEY[this.billing && this.billing.plan] || 'pricing.free';
      return this.$t(key);
    },
    statusKey() {
      return (this.billing && this.billing.status) || 'none';
    },
    // El ciclo de cuota no es el mes natural: empieza el día del alta y, si el
    // usuario pasa a premium, el día que empezó a pagar. El backend manda sus
    // dos extremos (usagePeriodStart/End), así que aquí no se calcula nada.
    periodLabel() {
      const from = this.cycleStart;
      const to = this.cycleEnd;
      if (!from || !to) return '';
      const day = (d) => d.toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
      return `${day(from)} – ${day(to)}`;
    },
    cycleStart() {
      const d = this.billing && this.billing.usagePeriodStart ? new Date(this.billing.usagePeriodStart) : null;
      return d && !isNaN(d.getTime()) ? d : null;
    },
    cycleEnd() {
      const d = this.billing && this.billing.usagePeriodEnd ? new Date(this.billing.usagePeriodEnd) : null;
      return d && !isNaN(d.getTime()) ? d : null;
    },
    resetDateLabel() {
      const d = this.cycleEnd;
      return d ? d.toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';
    },
    renewLabel() {
      if (!this.billing || !this.billing.periodEnd) return '';
      const date = new Date(this.billing.periodEnd).toLocaleDateString(this.$i18n.locale, { day: 'numeric', month: 'short', year: 'numeric' });
      const key = this.billing.cancelAtPeriodEnd ? 'dashboard.plan.cancels' : 'dashboard.plan.renews';
      return this.$t(key, { date });
    },
  },
  mounted() {
    this.load();
    // Vuelta del consentimiento de "conectar buzón" (redirect del backend).
    if (this.$route.query.mailbox === 'connected') {
      const email = this.$route.query.email || '';
      this.$toast.success(this.$t('oauthCallback.mailboxConnected', { email }));
      this.$router.replace({ query: {} }).catch(() => {});
    }
  },
  methods: {
    async load() {
      this.loading = true;
      this.errored = false;
      try {
        this.billing = await getBillingStatus();
      } catch (err) {
        this.errored = true;
      } finally {
        this.loading = false;
      }
    },
    fmt(micro) {
      const usd = (micro || 0) / MICRO_PER_USD;
      if (usd > 0 && usd < 0.01) return '<$0.01';
      return `$${usd.toFixed(2)}`;
    },
    async openPortal() {
      this.busy = true;
      try {
        const { url } = await billingPortal();
        if (url) window.location.href = url;
      } catch (err) {
        this.$toast.error(err.message || 'Portal unavailable');
      } finally {
        this.busy = false;
      }
    },
    goPlans() {
      this.$root.$emit('open-pricing');
    },
    async signOut() {
      await logout();
      clearSessionCookies(this.$cookies);
      this.$root.nukliUser = null;
      this.$router.push('/').catch(() => {});
    },
  },
};
</script>

<style scoped>
.account { background: var(--lp-bg); }
.account-inner { max-width: 920px; }

.account-head { margin-bottom: 36px; }
.account-sub { color: var(--lp-muted); font-size: 1.05rem; margin: 8px 0 0; }

.account-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px 0;
  color: var(--lp-muted);
}

.account-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}
.usage-card { grid-column: 1 / -1; }

.lp-card { padding: 24px; }

.card-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}
.card-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 600;
  font-size: 1.05rem;
  color: var(--lp-ink);
}
.plan-card .card-title,
.account-card .card-title { margin-bottom: 20px; }
.period-pill {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--lp-muted);
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  padding: 4px 10px;
  border-radius: 100px;
  text-transform: capitalize;
}

/* Usage meter */
.usage-numbers { display: flex; align-items: baseline; gap: 10px; margin-bottom: 12px; }
.usage-pct {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2.4rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1;
  color: var(--lp-ink);
}
.usage-pct small { font-size: 1.1rem; font-weight: 600; color: var(--lp-muted); margin-left: 1px; }
.usage-of { font-size: 0.9rem; color: var(--lp-muted); }

.meter-track {
  height: 10px;
  border-radius: 100px;
  background: var(--lp-bg-subtle);
  border: 1px solid var(--lp-line);
  overflow: hidden;
}
.meter-fill {
  height: 100%;
  border-radius: 100px;
  background: var(--lp-accent);
  transition: width 0.4s ease;
  min-width: 2px;
}
.meter-fill--warn { background: #f59e0b; }
.meter-fill--high { background: #ef4444; }

.usage-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 0.85rem;
  color: var(--lp-muted);
}
.usage-reset { display: inline-flex; align-items: center; gap: 6px; }

/* Breakdown */
.breakdown { margin-top: 24px; border-top: 1px solid var(--lp-line); padding-top: 20px; }
.breakdown-title {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--lp-muted);
  margin-bottom: 14px;
}
.breakdown-empty { color: var(--lp-muted); font-size: 0.9rem; margin: 0; }
.breakdown-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }
.breakdown-row { display: grid; grid-template-columns: 150px 1fr auto; align-items: center; gap: 14px; }
.breakdown-label { display: inline-flex; align-items: center; gap: 8px; font-size: 0.9rem; color: var(--lp-ink-2); }
.dot { width: 9px; height: 9px; border-radius: 50%; flex: 0 0 auto; }
.dot--frontier, .fill--frontier { background: var(--lp-accent); }
.dot--standard, .fill--standard { background: #6366f1; }
.dot--lite, .fill--lite { background: #06b6d4; }
.breakdown-bar { height: 7px; border-radius: 100px; background: var(--lp-bg-subtle); overflow: hidden; }
.breakdown-bar-fill { display: block; height: 100%; border-radius: 100px; min-width: 2px; }
.breakdown-cost { font-size: 0.85rem; font-weight: 600; color: var(--lp-ink); font-variant-numeric: tabular-nums; }

/* Plan */
.plan-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 20px; }
.plan-id { display: flex; flex-direction: column; gap: 3px; }
.plan-name { font-family: 'Space Grotesk', sans-serif; font-size: 1.5rem; font-weight: 700; letter-spacing: -0.02em; color: var(--lp-ink); }
.plan-meta { font-size: 0.85rem; color: var(--lp-muted); }
.status-badge {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 100px;
  background: var(--lp-bg-subtle);
  color: var(--lp-muted);
  border: 1px solid var(--lp-line);
  white-space: nowrap;
}
.status--active, .status--trialing { background: rgba(34, 197, 94, 0.12); color: #16a34a; border-color: transparent; }
.status--past_due, .status--incomplete { background: rgba(245, 158, 11, 0.14); color: #b45309; border-color: transparent; }
.status--canceled { background: rgba(239, 68, 68, 0.12); color: #dc2626; border-color: transparent; }

.model-access { list-style: none; padding: 0; margin: 0 0 22px; display: flex; flex-direction: column; gap: 9px; }
.model-access li { display: flex; align-items: center; gap: 9px; font-size: 0.9rem; color: var(--lp-ink-2); }
.model-access li .l-icon { color: #22c55e; }
.model-access li.locked { color: var(--lp-muted); }
.model-access li.locked .l-icon { color: var(--lp-muted); }

.plan-actions { display: flex; flex-wrap: wrap; gap: 10px; margin-top: auto; }
.plan-card, .account-card { display: flex; flex-direction: column; }

.card-btn { height: 44px; font-size: 0.92rem; flex: 1 1 auto; }

/* Account */
.acc-fields { margin: 0 0 22px; display: flex; flex-direction: column; gap: 16px; }
.acc-field { display: flex; flex-direction: column; gap: 3px; }
.acc-field dt { font-size: 0.78rem; font-weight: 600; letter-spacing: 0.03em; text-transform: uppercase; color: var(--lp-muted); margin: 0; }
.acc-field dd { margin: 0; font-size: 0.98rem; color: var(--lp-ink); word-break: break-word; }
/* flex 0: en la columna de la tarjeta, el flex-grow de .card-btn lo estiraba
   en vertical hasta rellenar la tarjeta (44px como el resto de botones). */
.signout { margin-top: auto; flex: 0 0 auto; }

@media (max-width: 720px) {
  .account-grid { grid-template-columns: 1fr; }
  .breakdown-row { grid-template-columns: 110px 1fr auto; gap: 10px; }
  .breakdown-label { font-size: 0.82rem; }
}
</style>
